import { chamferClipPath } from "@/lib/exploreImages";
import { cn } from "@/lib/utils";

// The worker app's industrial chip: chamfered corners, mono uppercase label
// with wide tracking. Interactive when given `onClick`.

// Light by default with `dark:` variants; copper reads on both.
const TONES = {
  default:
    "bg-black/[0.05] text-[#4C4F52] hover:bg-black/[0.09] dark:bg-white/[0.06] dark:text-[#CCCCCC] dark:hover:bg-white/[0.12]",
  active: "bg-[#212327] text-[#FAFAFA] dark:bg-[#FAFAFA] dark:text-[#212327]",
  copper: "bg-[#D17949] text-[#FFF5F0]",
  outline:
    "bg-transparent text-[#77787A] ring-1 ring-inset ring-black/15 dark:text-[#9E9E9E] dark:ring-white/15",
  // Set on top of a photo, so the same in both themes.
  overlay: "bg-white/15 text-white backdrop-blur-sm",
  overlayDark: "bg-[#212327]/80 text-[#DDDDDD]",
} as const;

export const ExploreImageChip = ({
  className,
  count,
  label,
  onClick,
  size = "md",
  tone = "default",
}: {
  className?: string;
  count?: number;
  label: string;
  onClick?: () => void;
  size?: "sm" | "md";
  tone?: keyof typeof TONES;
}) => {
  const classes = cn(
    "inline-flex shrink-0 items-center gap-2 font-plex-mono uppercase tracking-[0.12em] transition-colors",
    size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-3 py-1.5 text-[11px]",
    TONES[tone],
    onClick && "cursor-pointer",
    className,
  );
  const content = (
    <>
      {label}
      {count !== undefined && (
        <span className={tone === "active" ? "opacity-60" : "opacity-50"}>
          {count}
        </span>
      )}
    </>
  );
  const style = { clipPath: chamferClipPath(size === "sm" ? 4 : 6) };

  if (!onClick)
    return (
      <span className={classes} style={style}>
        {content}
      </span>
    );
  return (
    <button className={classes} onClick={onClick} style={style} type="button">
      {content}
    </button>
  );
};
